/**
 * `RouteScaffold` — explicit "this surface is reserved for a later WP-E*"
 * landing region used by every route in `routes.tsx` whose final
 * implementation has not yet landed on `release/2026-q2-geoviewer`.
 *
 * Per WP-E1 dispatch §4.2.a–d, the scaffold:
 *   a. Renders a token-only landing region with the screen id, route
 *      path, and a one-paragraph "This surface will be implemented in
 *      WP-E<N>" stub message (provided via `wpId`).
 *   b. Sets `document.title` per route on mount.
 *   c. Calls `useLiveRegion().announce('status', '<route> mounted')`
 *      once on mount so a11y QA can audit announcement behaviour in
 *      WP-E13 without waiting for full implementations.
 *   d. Carries a `data-gvw-id="<GVW-id>"` attribute so the route-table
 *      test can assert resolution.
 *
 * IMPORTANT: this is the ONLY component allowed to claim "WP-E2..WP-E12
 * will deliver". Do NOT replace it with a half-built placeholder that
 * pretends to work — the scaffold makes the unimplemented status
 * explicit and discoverable for both human reviewers and screen readers.
 *
 * Cite:
 *   - dispatch `WP-E1-routes-foundation-1.md` §4 step 2
 *   - `architecture.md` §3.2 (route table)
 *   - `navigation-map.md` §1 (GVW-* ids)
 *   - `DESIGN_POLICY.md` §5 (token-only styling, non-color cue)
 */

import { useEffect, useRef } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export interface RouteScaffoldProps {
  /** Stable screen id from `navigation-map.md` §1 (e.g. "GVW-160"). */
  gvwId: string;
  /** Canonical route path (e.g. "/no-orgs"). */
  route: string;
  /** Short human title; used as the `document.title` and the H1. */
  title: string;
  /** Optional subhead overriding the default "Reserved surface" copy. */
  subhead?: string;
  /** Which later WP-E* will deliver this surface (e.g. "WP-E2"). */
  wpId?: string;
  /** Optional override for the body copy. Defaults to a stub paragraph. */
  body?: string;
  /** Stable test id; defaults to `route-scaffold`. */
  'data-testid'?: string;
}

function defaultBody(wpId: string | undefined, route: string): string {
  const owner = wpId ?? 'a later WP-E* dispatch';
  return (
    `Reserved surface. Route ${route} is enumerated in the GeoViewer ` +
    `architecture route table for Phase 2 but its final implementation ` +
    `will be delivered in ${owner}. Until then this scaffold is mounted ` +
    `as the route element so the URL resolves, deep-link grammar dispatches ` +
    `correctly, and screen-reader announcements remain consistent.`
  );
}

export function RouteScaffold({
  gvwId,
  route,
  title,
  subhead = 'Reserved surface',
  wpId,
  body,
  'data-testid': testId = 'route-scaffold',
}: RouteScaffoldProps) {
  const liveRegion = useLiveRegion();
  const announcedRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = `${title} · GeoViewer`;
    }
    if (announcedRef.current === route) return;
    announcedRef.current = route;
    liveRegion.announce('status', `${route} mounted`);
  }, [liveRegion, route, title]);

  const copy = body ?? defaultBody(wpId, route);

  return (
    <main
      role="main"
      aria-labelledby={`${gvwId}-heading`}
      data-gvw-id={gvwId}
      data-route={route}
      data-testid={testId}
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.info}`,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[5]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[3],
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.sm,
            lineHeight: tokens.font.lineHeight.tight,
            color: tokens.color.ink.secondary,
            fontFamily: tokens.font.family.mono,
            letterSpacing: 0.5,
          }}
        >
          {gvwId} · {route}
        </p>
        <h1
          id={`${gvwId}-heading`}
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            fontWeight: tokens.font.weight.medium,
          }}
        >
          {subhead}
        </p>
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.primary,
          }}
        >
          {copy}
        </p>
      </section>
    </main>
  );
}
